package gr.tech.GRAttestation.components.layouts

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.style.CssStyle
import gr.tech.GRAttestation.components.widgets.loadingSpinnerMaxSize
import gr.tech.GRAttestation.infrastructure.api.adminUserLogout
import gr.tech.GRAttestation.infrastructure.api.checkUserJWT
import gr.tech.GRAttestation.infrastructure.session.AdminUserSession
import gr.tech.GRAttestation.models.user.USER_ROLES
import gr.tech.GRAttestation.navigation.AdminSitePage
import gr.tech.GRAttestation.navigation.SitePage
import gr.tech.GRAttestation.sections.admin.AdminSideNav
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.vw

val AdminPageContentStyle = CssStyle {
    base {
        Modifier
            .fillMaxSize()
    }
}

@Composable
fun rememberSideNavState(): MutableState<Boolean> {
    val key = "admin-side-nav-open"
    val default = window.localStorage.getItem(key)?.toBooleanStrictOrNull() ?: true
    val state = remember { mutableStateOf(default) }

    LaunchedEffect(state.value) {
        window.localStorage.setItem(key, state.value.toString())
    }

    return state
}


@Composable
fun AdminPageLayout(
    title: String = "Admin",
    showNav: Boolean = true,
    redirectIfLoggedIn: String? = null,
    requiredRoles: List<String> = listOf(USER_ROLES.CONFIRMED_USER.role),
    content: @Composable ColumnScope.(ctx: PageContext) -> Unit
) {
    val scope = rememberCoroutineScope()
    val ctx = rememberPageContext()
    var hasValidAccess by remember { mutableStateOf(AdminUserSession.checked.value) }
    // Changed this for if user has been checked

    //var userHasAccess by remember { mutableStateOf(false) }
    // Trying to find what makes this better than accessCheckComplete?

    val sideNavState = rememberSideNavState()

    LaunchedEffect(Unit) {
        document.title = title

        if (!hasValidAccess) { // Used to be !UserSession.checked.value
            val userPreviewInfo = checkUserJWT(
                redirectLogin = false,
                redirectTo = AdminSitePage.Login.route
            )
            if (userPreviewInfo.success && userPreviewInfo.data != null) {
                AdminUserSession.info.value = userPreviewInfo.data
            } else {
                AdminUserSession.info.value = null
            }

            AdminUserSession.checked.value = true
        }

        val isLoggedIn = AdminUserSession.isLoggedIn()

        // ----- REDIRECTION LOGIC -----

        // If the user is logged-in and you provided a redirect path (e.g. from login/signup),
        // send them there instead of letting them access this page.
        if (isLoggedIn && redirectIfLoggedIn != null) {
            ctx.router.tryRoutingTo(redirectIfLoggedIn)
            return@LaunchedEffect
        }

        // If user is not logged in and auth is required, bounce to login
        if (!isLoggedIn && requiredRoles.isNotEmpty()) {
            ctx.router.tryRoutingTo(AdminSitePage.Login.route)
            return@LaunchedEffect
        }

        // ----- ROLE CHECK LOGIC -----

        // If roles are required, ensure the current user has at least one of them
        if (requiredRoles.isNotEmpty()) {
            val userRoles = AdminUserSession.info.value?.roles ?: emptyList()

            val hasMatchingRole = userRoles.any { it in requiredRoles }

            if (!hasMatchingRole) {
                // User is logged in but doesn't have permission → bounce home
                ctx.router.tryRoutingTo(SitePage.Dashboard.route)
                return@LaunchedEffect
            }
        }

        // User passed all checks (or no checks required)
        //userHasAccess = true
        hasValidAccess = true
    }


    Box(
        Modifier
            .width(100.vw)
            .minHeight(100.percent),
        contentAlignment = Alignment.Center
    ) {
        Row(
            Modifier.fillMaxSize(),
            horizontalArrangement = Arrangement.Start
        ) {
            if (hasValidAccess || (requiredRoles.isEmpty())) { //Change to OR, and took out (requiredRoles.isEmpty() || hasAccess)
                if (showNav) {
                    AdminSideNav(
                        currentRoute = ctx.route.path.removeSuffix("/"),
                        isOpen = sideNavState.value,
                        onToggleOpened = {
                            sideNavState.value = !sideNavState.value
                        },
                        onLogout = { scope.launch { adminUserLogout() } }
                    )
                }

                Column(
                    Modifier
                        .fillMaxHeight()
                        .overflow(Overflow.Hidden)
                        .weight(1f),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    content(ctx)
                }
            } else {
                loadingSpinnerMaxSize()
            }
        }
    }
}